import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, PartnerDto, CreatePartnerDto } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { Observable, filter, take } from 'rxjs';

@Component({
  selector: 'app-partners',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './partners.component.html',
  styleUrl: './partners.component.css'
})
export class PartnersComponent implements OnInit {
  partners: PartnerDto[] = [];
  expandedRows: Set<string> = new Set();
  loading = false;
  errorMessage = '';
  
  // Form state for create/edit
  showCreateModal = false;
  showEditModal = false;
  selectedPartner: PartnerDto | null = null;
  partnerForm: CreatePartnerDto = {
    name: '',
    address: '',
    contactPerson: '',
    contactNumber: '',
    alternateContactNumber: null,
    emailId: null
  };

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading partners
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadPartners();
      });
  }

  loadPartners(): void {
    this.loading = true;
    this.errorMessage = '';
    
    this.externalDataService.getAllPartners().subscribe({
      next: (partners) => {
        this.partners = partners;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading partners:', error);
        this.errorMessage = 'Failed to load partners. Please try again.';
        this.loading = false;
        this.partners = [];
      }
    });
  }

  toggleRow(partnerId: string): void {
    if (this.expandedRows.has(partnerId)) {
      this.expandedRows.delete(partnerId);
    } else {
      this.expandedRows.add(partnerId);
    }
  }

  isRowExpanded(partnerId: string): boolean {
    return this.expandedRows.has(partnerId);
  }

  // Action methods
  openCreateModal(): void {
    this.partnerForm = {
      name: '',
      address: '',
      contactPerson: '',
      contactNumber: '',
      alternateContactNumber: null,
      emailId: null
    };
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.partnerForm = {
      name: '',
      address: '',
      contactPerson: '',
      contactNumber: '',
      alternateContactNumber: null,
      emailId: null
    };
  }

  openEditModal(partner: PartnerDto): void {
    this.selectedPartner = partner;
    this.partnerForm = {
      name: partner.name,
      address: partner.address,
      contactPerson: partner.contactPerson,
      contactNumber: partner.contactNumber,
      alternateContactNumber: partner.alternateContactNumber || null,
      emailId: partner.emailId || null
    };
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedPartner = null;
    this.partnerForm = {
      name: '',
      address: '',
      contactPerson: '',
      contactNumber: '',
      alternateContactNumber: null,
      emailId: null
    };
  }

  createPartner(): void {
    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.externalDataService.createPartner(this.partnerForm).subscribe({
      next: (partner) => {
        this.loadPartners();
        this.closeCreateModal();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error creating partner:', error);
        this.errorMessage = 'Failed to create partner. Please try again.';
        this.loading = false;
      }
    });
  }

  updatePartner(): void {
    if (!this.selectedPartner || !this.validateForm()) {
      return;
    }

    this.loading = true;
    const updateData: PartnerDto = {
      ...this.selectedPartner,
      ...this.partnerForm
    };

    this.externalDataService.updatePartner(updateData).subscribe({
      next: (partner) => {
        this.loadPartners();
        this.closeEditModal();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error updating partner:', error);
        this.errorMessage = 'Failed to update partner. Please try again.';
        this.loading = false;
      }
    });
  }

  validateForm(): boolean {
    if (!this.partnerForm.name?.trim()) {
      this.errorMessage = 'Partner name is required.';
      return false;
    }
    if (!this.partnerForm.address?.trim()) {
      this.errorMessage = 'Address is required.';
      return false;
    }
    if (!this.partnerForm.contactPerson?.trim()) {
      this.errorMessage = 'Contact person is required.';
      return false;
    }
    if (!this.partnerForm.contactNumber?.trim()) {
      this.errorMessage = 'Contact number is required.';
      return false;
    }
    this.errorMessage = '';
    return true;
  }
}



